import { gql } from "@apollo/client";
import { Box, Field, Spinner, Text, useFilter, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import {
  AutoComplete,
  AutoCompleteCreatable,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
  AutoCompleteTag,
} from "@choc-ui/chakra-autocomplete";
import { useState } from "react";
import {
  type ShopifyExhibitionSettingForm_ShopifySettingFragment,
  useListPorductTypeHistoriesLazyQuery,
  useListTagHistoriesLazyQuery,
  useListVendorHistoriesLazyQuery,
} from "../graphql/generated/apollo";
import { useShopContext } from "../shop";

gql`
  query ListPorductTypeHistories($shopId: uuid!) {
    exhibition_shopify_settings(distinct_on: product_type, where: {exhibition: {shop_id: {_eq: $shopId}}}) {
      product_type
    }
  }
  query ListVendorHistories($shopId: uuid!) {
    exhibition_shopify_settings(distinct_on: vendor, where: {exhibition: {shop_id: {_eq: $shopId}}}) {
      vendor
    }
  }
  query ListTagHistories($shopId: uuid!) {
    exhibition_shopify_settings(where: {exhibition: {shop_id: {_eq: $shopId}}}) {
      tags
    }
  }
  fragment ShopifyExhibitionSettingForm_shopifySetting on exhibition_shopify_settings {
    category
    product_type
    vendor
    tags
  }
`;

type ShopifyExhibitionSettingFormProps = {
  shopifySetting?: ShopifyExhibitionSettingForm_ShopifySettingFragment;
  setShopifySetting: (newState: ShopifyExhibitionSettingForm_ShopifySettingFragment) => void;
};

export function ShopifyExhibitionSettingForm({
  shopifySetting = {
    category: "",
    product_type: "",
    vendor: "",
    tags: [],
  },
  setShopifySetting,
}: ShopifyExhibitionSettingFormProps) {
  const { currentShopId } = useShopContext();
  const filter = useFilter({ sensitivity: "base" });

  const [listProductTypeHistoriesQuery, { data: productTypeHistoriesData, loading: productTypeHistoriesLoading }] =
    useListPorductTypeHistoriesLazyQuery({
      variables: {
        shopId: currentShopId ?? "",
      },
    });

  const [listVendorHistoriesQuery, { data: vendorHistoriesData, loading: vendorHistoriesLoading }] =
    useListVendorHistoriesLazyQuery({
      variables: {
        shopId: currentShopId ?? "",
      },
    });

  const [listTagHistoriesQuery, { data: tagHistoriesData, loading: tagHistoriesLoading }] =
    useListTagHistoriesLazyQuery({
      variables: {
        shopId: currentShopId ?? "",
      },
    });

  const [tagInputValue, setTagInputValue] = useState("");
  const removeTag = (index: number) => {
    setShopifySetting({ ...shopifySetting, tags: shopifySetting.tags.filter((_: string, i: number) => i !== index) });
  };

  // タグ履歴から全てのタグを取得し、重複を除去
  const allHistoricalTags =
    tagHistoriesData?.exhibition_shopify_settings
      ?.flatMap((setting) => setting.tags || [])
      .filter((tag, index, array) => array.findIndex((t) => t.toLowerCase() === tag.toLowerCase()) === index) || [];
  const appendTag = (tagInputValue: string | string[]) => {
    const trimmedValues =
      typeof tagInputValue === "string"
        ? tagInputValue
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : Array.isArray(tagInputValue)
          ? tagInputValue.map((v) => v.trim()).filter(Boolean)
          : [];
    if (trimmedValues.length > 0) {
      setShopifySetting({
        ...shopifySetting,
        tags: [...shopifySetting.tags, ...trimmedValues.filter((v) => !shopifySetting.tags.includes(v))],
      });
    }
  };

  return (
    <VStack>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">商品タイプ {shopifySetting.product_type}</Text>
        </Field.Label>
        <AutoComplete
          openOnFocus
          creatable
          listAllValuesOnFocus
          suggestWhenEmpty
          prefocusFirstItem={false}
          restoreOnBlurIfEmpty={false}
          isLoading={productTypeHistoriesLoading}
          value={shopifySetting.product_type}
          onChange={(value: string) => {
            setShopifySetting({ ...shopifySetting, product_type: value.trim() });
          }}
        >
          <AutoCompleteInput
            placeholder="商品タイプを入力"
            onFocus={() => {
              listProductTypeHistoriesQuery();
            }}
            onBlur={(e) => {
              const trimmedValue = e.target.value.trim();
              if (trimmedValue !== shopifySetting.product_type) {
                setShopifySetting({ ...shopifySetting, product_type: trimmedValue });
              }
            }}
          />
          <AutoCompleteList>
            <AutoCompleteCreatable>{({ value }) => <span>{value}（新規追加）</span>}</AutoCompleteCreatable>
            {productTypeHistoriesLoading ? (
              <Box p={4} textAlign="center">
                <Spinner />
              </Box>
            ) : (
              productTypeHistoriesData?.exhibition_shopify_settings
                ?.filter((history) => history.product_type)
                ?.map((history) => (
                  <AutoCompleteItem
                    key={history.product_type}
                    value={history.product_type}
                    label={history.product_type}
                  >
                    {history.product_type}
                  </AutoCompleteItem>
                ))
            )}
          </AutoCompleteList>
        </AutoComplete>
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">販売元</Text>
        </Field.Label>
        <AutoComplete
          openOnFocus
          creatable
          listAllValuesOnFocus
          suggestWhenEmpty
          prefocusFirstItem={false}
          restoreOnBlurIfEmpty={false}
          isLoading={vendorHistoriesLoading}
          value={shopifySetting.vendor}
          onChange={(value: string) => {
            setShopifySetting({ ...shopifySetting, vendor: value.trim() });
          }}
        >
          <AutoCompleteInput
            placeholder="販売元を入力"
            onFocus={() => {
              listVendorHistoriesQuery();
            }}
            onBlur={(e) => {
              const trimmedValue = e.target.value.trim();
              if (trimmedValue !== shopifySetting.vendor) {
                setShopifySetting({ ...shopifySetting, vendor: trimmedValue });
              }
            }}
          />
          <AutoCompleteList>
            <AutoCompleteCreatable>{({ value }) => <span>{value}（新規追加）</span>}</AutoCompleteCreatable>
            {vendorHistoriesLoading ? (
              <Box p={4} textAlign="center">
                <Spinner />
              </Box>
            ) : (
              vendorHistoriesData?.exhibition_shopify_settings
                ?.filter((history) => history.vendor)
                ?.map((history) => (
                  <AutoCompleteItem key={history.vendor} value={history.vendor} label={history.vendor}>
                    {history.vendor}
                  </AutoCompleteItem>
                ))
            )}
          </AutoCompleteList>
        </AutoComplete>
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">タグ（カンマ区切り）</Text>
        </Field.Label>
        <AutoComplete
          multiple
          freeSolo
          openOnFocus
          creatable
          listAllValuesOnFocus
          suggestWhenEmpty
          prefocusFirstItem={false}
          restoreOnBlurIfEmpty={false}
          isLoading={tagHistoriesLoading}
          value={tagInputValue}
          onChange={(values) => {
            appendTag(values);
          }}
          onCreateOption={(e) => {
            appendTag(e.item.value);
          }}
        >
          <AutoCompleteInput
            placeholder="タグを入力"
            onFocus={() => {
              listTagHistoriesQuery();
            }}
            onChange={(e) => {
              setTagInputValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && tagInputValue.trim()) {
                e.preventDefault();
                appendTag(tagInputValue);
                setTagInputValue("");
              }
            }}
            onBlur={(e) => {
              appendTag(e.target.value);
              setTagInputValue("");
            }}
          />
          <AutoCompleteList>
            {tagHistoriesLoading ? (
              <Box p={4} textAlign="center">
                <Spinner />
              </Box>
            ) : (
              <>
                <AutoCompleteCreatable>
                  {({ value }: { value: string }) => (
                    <span>
                      {value
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean)
                        .filter((v) => !allHistoricalTags.includes(v))
                        .map((v) => `"${v}"`)
                        .join(",")}
                      （新規追加）
                    </span>
                  )}
                </AutoCompleteCreatable>
                {allHistoricalTags
                  ?.filter((tag) => !shopifySetting.tags.includes(tag))
                  ?.filter((tag) => !tagInputValue || tag.toLowerCase().includes(tagInputValue.toLowerCase()))
                  ?.slice(0, 8)
                  ?.map((tag) => (
                    <AutoCompleteItem key={tag} value={tag} label={tag}>
                      {tag}
                    </AutoCompleteItem>
                  ))}
              </>
            )}
          </AutoCompleteList>
        </AutoComplete>
        <Wrap gap={2} mt={1}>
          {shopifySetting.tags.map((tag: string, index: number) => (
            <WrapItem key={tag}>
              <AutoCompleteTag label={tag} onRemove={() => removeTag(index)} size="lg" />
            </WrapItem>
          ))}
        </Wrap>
      </Field.Root>
    </VStack>
  );
}
